<template>
  <div
    class="atom-datepicker--container"
    :class="{ datepicker__error: isError }"
  >
    <div
      class="d-flex align-items-center"
      :class="[
        !title ? 'justify-content-end' : 'justify-content-between',
        {
          'custom-margin-bottom-title':
            showSetTimeNowButton || isClearable || title,
        },
      ]"
    >
      <div v-if="title" class="d-flex atom-datepicker--title">
        {{ title }}
        <span v-if="isAsterisk" class="atom-datepicker--asterisk">*</span>
      </div>
      <div v-if="!isDisabled" class="d-flex align-items-center">
        <div
          v-if="showSetTimeNowButton"
          class="d-flex"
          @click="setDatetimeToCurrent"
        >
          <span class="set-current-datetime-btn"> Now </span>
        </div>
        <span
          v-if="showSetTimeNowButton && isClearable"
          class="font-color-grey font-size-10"
        >
          &nbsp;|&nbsp;
        </span>
        <div v-if="isClearable" class="d-flex" @click="datepicker = null">
          <span class="font-color-grey font-size-10 cursor-pointer">
            Clear
          </span>
        </div>
      </div>
    </div>
    <flat-pickr
      v-model="datepicker"
      class="form-control"
      :id="datepickerId"
      :disabled="isDisabled"
      :config="setConfig"
      :placeholder="placeholder"
    ></flat-pickr>
    <feather-icon
      class="atom-datepicker--icon"
      :class="positionIcon"
      icon="CalendarIcon"
      stroke="#B9B9C3"
    ></feather-icon>
    <div v-if="isError" class="error-message">{{ errorMessage }}</div>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import flatPickr from "vue-flatpickr-component"

export default {
  name: "AtomDatepicker",
  components: {
    flatPickr,
  },
  props: {
    datepickerId: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: "date",
      // value: ["date", "time", "dateNtime"]
    },
    placeholder: {
      type: String,
      require: false,
      default: "",
    },
    minDate: {
      type: String,
      required: false,
      default: null,
    },
    maxDate: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    isAsterisk: {
      type: Boolean,
      required: false,
      default: false,
    },
    isError: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorMessage: {
      type: String,
      required: false,
      default: null,
    },
    value: {
      type: [String, null],
      required: false,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isClearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSetTimeNowButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["input", "onChangeDatetime"],
  computed: {
    datepicker: {
      get() {
        return this.value
      },
      set(value) {
        if (this.type === "time") {
          this.$emit("input", value)
        } else if (value) {
          const dateTime = new Date(value)
          this.$emit("input", DateTime.fromJSDate(dateTime).toISO())
        } else {
          this.$emit("input", value)
        }
      },
    },
    setConfig() {
      if (this.type === "time") {
        return {
          enableTime: true,
          noCalendar: true,
          dateFormat: "H:i",
          maxDate: this.maxDate,
          minDate: this.minDate,
          disableMobile: this.isDisabled,
        }
      } else if (this.type === "dateNtime") {
        return {
          enableTime: true,
          dateFormat: "Y-m-d H:i",
          maxDate: this.maxDate,
          minDate: this.minDate,
          disableMobile: this.isDisabled,
        }
      }
      return {
        maxDate: this.minDate,
        minDate: this.maxDate,
        disableMobile: this.isDisabled,
      }
    },
    positionIcon() {
      if (this.isClearable) return "icon-padding-top-clear"
      if (this.title) return "icon-padding-top"
      return undefined
    },
  },
  watch: {
    datepicker: {
      deep: true,
      handler() {
        this.$emit("onChangeDatetime")
      },
    },
  },
  methods: {
    setDatetimeToCurrent() {
      if (this.type === "date") {
        this.datepicker = DateTime.now().toISODate()
      } else if (this.type === "time") {
        this.datepicker = DateTime.now().toFormat("HH:mm")
      } else if (this.type === "dateNtime") {
        this.datepicker = DateTime.now().toISO()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-flatpicker.scss";
.atom-datepicker--container {
  position: relative;
}
.atom-datepicker--icon {
  position: absolute;
  top: 9px;
  right: 14px;
  width: 20px;
  height: 20px;
}

.icon-padding-top {
  top: 31px;
}

.icon-padding-top-clear {
  top: 31px;
}

.atom-datepicker--title {
  color: #5e5873;
  font-size: 12px;
  line-height: 1.5;
}

.custom-margin-bottom-title {
  margin-bottom: 4px;
}

.atom-datepicker--asterisk {
  color: #ea5455;
  font-size: 12px;
  margin-bottom: 0;
}

.error-message {
  margin-top: 4px;
  line-height: 1.5;
  font-size: 12px;
  color: #ea5455;
}

.flatpickr-input[readonly]:disabled {
  background-color: #efefef !important;
  pointer-events: none;
}

.datepicker__error .flatpickr-input[readonly] {
  border-color: #ea5455;
}

.set-current-datetime-btn {
  font-size: 10px;
  &:hover {
    cursor: pointer;
    color: #7367f0;
    text-decoration: underline;
  }
}
</style>
